<template>
  <div class="profile-wrapper">
    <!-- Profile Header with Cover Image -->
    <div class="profile-header">
      <div class="cover-image">
        <div class="cover-overlay"></div>
        <div class="profile-avatar">
          <div class="avatar-wrapper">
            <img
                v-if="user && user.profile_image"
                :src="profileImage"
                :alt="user.name"
                class="avatar-image"
            />
            <div v-else class="avatar-placeholder">
              {{ getUserInitials(user && user.name) }}
            </div>
            <div class="avatar-status" :class="{'online': user && user.is_online}">
              <span class="status-dot"></span>
            </div>
          </div>
        </div>
        <div class="profile-actions">
          <md-button class="md-primary md-round edit-btn" @click="goToEdit(user && user.id)">
            <md-icon>edit</md-icon>
            Edit Profile
          </md-button>
        </div>
      </div>
    </div>

    <!-- Profile Content -->
    <div class="profile-content">
      <div class="profile-main">
        <div class="profile-info">
          <h1 class="profile-name">
            {{ user ? user.name : 'User' }}
          </h1>
          <div class="profile-badges">
            <span class="badge badge-vendor" v-if="user && user.model && user.model.vendor">
              <md-icon>business</md-icon>
              {{ user.model.vendor.busines_name || 'Vendor' }}
            </span>
            <span class="badge badge-role" v-for="role in (user && user.roles || [])" :key="role.id">
              <md-icon>verified_user</md-icon>
              {{ role.name }}
            </span>
          </div>
          <badge type="info">{{ user && user.model && user.model.vendor ? user.model.vendor.name : user && user.name || 'User' }}</badge>
          <p class="profile-description" v-if="user && user.model && user.model.vendor && user.model.vendor.description">
            {{ user.model.vendor.description }}
          </p>
        </div>

        <!-- Quick Stats -->
        <div class="quick-stats">
          <div class="stat-item">
            <span class="stat-icon">
              <md-icon>email</md-icon>
            </span>
            <div class="stat-info">
              <span class="stat-label">Email</span>
              <span class="stat-value">{{ user && user.email || 'N/A' }}</span>
            </div>
          </div>
          <div class="stat-item">
            <span class="stat-icon">
              <md-icon>phone</md-icon>
            </span>
            <div class="stat-info">
              <span class="stat-label">Phone</span>
              <span class="stat-value">{{ user && user.model && user.model.vendor ? user.model.vendor.phone_number : 'N/A' }}</span>
            </div>
          </div>
          <div class="stat-item">
            <span class="stat-icon">
              <md-icon>location_on</md-icon>
            </span>
            <div class="stat-info">
              <span class="stat-label">Address</span>
              <span class="stat-value">{{ user && user.model && user.model.vendor ? user.model.vendor.address : 'N/A' }}</span>
            </div>
          </div>
          <div class="stat-item">
            <span class="stat-icon">
              <md-icon>people</md-icon>
            </span>
            <div class="stat-info">
              <span class="stat-label">Roles</span>
              <span class="stat-value">{{ user && user.roles ? user.roles.length : 0 }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Profile Details Grid -->
      <div class="profile-details-grid">
        <!-- Vendor Details -->
        <div class="detail-card" v-if="user && user.model && user.model.vendor">
          <div class="detail-card-header">
            <md-icon>store</md-icon>
            <h3>Vendor Information</h3>
          </div>
          <div class="detail-card-body">
            <div class="detail-row">
              <span class="detail-label">Business Name</span>
              <span class="detail-value">{{ user.model.vendor.busines_name || 'N/A' }}</span>
            </div>
            <div class="detail-row">
              <span class="detail-label">Email</span>
              <span class="detail-value">{{ user.model.vendor.email || 'N/A' }}</span>
            </div>
            <div class="detail-row">
              <span class="detail-label">Telephone</span>
              <span class="detail-value">{{ user.model.vendor.telephone_number || 'N/A' }}</span>
            </div>
            <div class="detail-row">
              <span class="detail-label">Mobile</span>
              <span class="detail-value">{{ user.model.vendor.phone_number || 'N/A' }}</span>
            </div>
            <div class="detail-row">
              <span class="detail-label">Address</span>
              <span class="detail-value">{{ user.model.vendor.address || 'N/A' }}</span>
            </div>
            <div class="detail-row" v-if="user.model.vendor.website">
              <span class="detail-label">Website</span>
              <span class="detail-value">
                <a :href="user.model.vendor.website" target="_blank">{{ user.model.vendor.website }}</a>
              </span>
            </div>
          </div>
        </div>

        <!-- Signature Section -->
        <div class="detail-card">
          <div class="detail-card-header">
            <md-icon>draw</md-icon>
            <h3>Signature</h3>
          </div>
          <div class="detail-card-body signature-body">
            <div v-if="user && user.signature_url" class="signature-preview">
              <img :src="user.signature_url" alt="Signature" class="signature-image" />
              <div class="signature-date" v-if="user && user.signature_updated_at">
                <md-icon>schedule</md-icon>
                Updated: {{ formatDate(user.signature_updated_at) }}
              </div>
            </div>
            <div v-else class="signature-empty">
              <md-icon>draw</md-icon>
              <p>No signature uploaded</p>
            </div>
            <md-button class="md-primary md-round signature-btn" @click="goToEdit(user && user.id)">
              <md-icon>edit</md-icon>
              {{ user && user.signature_url ? 'Update Signature' : 'Add Signature' }}
            </md-button>
          </div>
        </div>

        <!-- Roles Section -->
        <div class="detail-card detail-card-full">
          <div class="detail-card-header">
            <md-icon>security</md-icon>
            <h3>Roles & Permissions</h3>
          </div>
          <div class="detail-card-body roles-body">
            <div v-if="user && user.roles && user.roles.length > 0" class="roles-grid">
              <div v-for="role in user.roles" :key="role.id" class="role-item">
                <div class="role-icon">
                  <md-icon>verified</md-icon>
                </div>
                <div class="role-info">
                  <span class="role-name">{{ role.name }}</span>
                  <span class="role-description" v-if="role.description">{{ role.description }}</span>
                </div>
              </div>
            </div>
            <div v-else class="roles-empty">
              <md-icon>shield</md-icon>
              <p>No roles assigned</p>
            </div>
          </div>
        </div>
      </div>

      <!-- Actions Footer -->
      <div class="profile-actions-footer">
        <md-button class="md-primary md-raised" @click="openPasswordModal">
          <md-icon>lock</md-icon>
          Change Password
        </md-button>
        <md-button class="md-info md-raised" @click="refreshProfile">
          <md-icon>refresh</md-icon>
          Refresh Profile
        </md-button>
      </div>
    </div>


  </div>
</template>

<script>
import moment from 'moment';
import {Badge} from "@/components";

export default {
  name: 'UserProfileCard',
  components: {Badge},
  props: {
    cardUserImage: {
      type: String,
      default: process.env.VUE_APP_BASE_URL + '/img/faces/marc.jpg'
    },
    showPasswordModal: Boolean,
    buttonColor: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      user: null,
      isLoading: false
    };
  },
  computed: {
    profileImage() {
      if (this.user && this.user.profile_image) {
        return process.env.VUE_APP_IMG_URL + this.user.profile_image;
      }
      return this.cardUserImage;
    }
  },
  created() {
    this.getProfile();
  },
  methods: {
    async getProfile() {
      try {
        this.isLoading = true;
        await this.$store.dispatch('profile/me');
        this.user = await this.$store.getters['profile/me'];
        this.isLoading = false;
      } catch (error) {
        console.error('Error fetching profile:', error);
        this.isLoading = false;
      }
    },

    async refreshProfile() {
      await this.getProfile();
      this.notifySuccess('Profile refreshed successfully');
    },

    goToEdit(id) {
      if (id) {
        this.$router.push({name: 'Edit Signature', params: {id}});
      } else {
        this.notifyWarning('User ID not found');
      }
    },

    getUserInitials(name) {
      if (!name) return 'U';
      const names = name.split(' ');
      if (names.length >= 2) {
        return (names[0][0] + names[1][0]).toUpperCase();
      }
      return name.substring(0, 2).toUpperCase();
    },

    getColorButton(buttonColor) {
      return 'md-' + buttonColor + '';
    },

    formatDate(date) {
      if (!date) return 'N/A';
      return moment(date).format('DD MMM YYYY, HH:mm');
    },


    notifySuccess(message) {
      this.$store.dispatch('alerts/success', message);
    },

    notifyWarning(message) {
      this.$store.dispatch('alerts/warning', message);
    },

    openPasswordModal() {
      console.log('openPasswordModal');
      this.$emit('update:show-password-modal', true);
    }
  }
};
</script>

<style scoped>
.profile-wrapper {
  max-width: 100%;
  margin: 0 auto;
  background: #f5f7fa;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.08);
}

/* ========== Profile Header ========== */
.profile-header {
  position: relative;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  padding: 40px 40px 80px 40px;
}

.cover-image {
  position: relative;
  min-height: 200px;
}

.cover-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(135deg, rgba(102, 126, 234, 0.85) 0%, rgba(118, 75, 162, 0.85) 100%);
}

.profile-avatar {
  position: relative;
  z-index: 2;
  display: flex;
  justify-content: center;
}

.avatar-wrapper {
  position: relative;
  width: 140px;
  height: 140px;
  border-radius: 50%;
  border: 6px solid rgba(255, 255, 255, 0.3);
  overflow: hidden;
  background: #fff;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.15);
}

.avatar-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.avatar-placeholder {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 48px;
  font-weight: 600;
  color: #667eea;
  background: linear-gradient(135deg, #e0e7ff 0%, #f0f0ff 100%);
}

.avatar-status {
  position: absolute;
  bottom: 8px;
  right: 8px;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: #e0e0e0;
  border: 3px solid #fff;
  display: flex;
  align-items: center;
  justify-content: center;
}

.avatar-status.online {
  background: #4caf50;
}

.status-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #fff;
}

.profile-actions {
  position: absolute;
  top: 20px;
  right: 20px;
  z-index: 3;
}

.edit-btn {
  background: rgba(255, 255, 255, 0.2) !important;
  color: #fff !important;
  backdrop-filter: blur(10px);
}

.edit-btn:hover {
  background: rgba(255, 255, 255, 0.3) !important;
}

/* ========== Profile Content ========== */
.profile-content {
  padding: 0 40px 40px;
  margin-top: -60px;
  position: relative;
  z-index: 1;
}

.profile-main {
  background: #fff;
  border-radius: 16px;
  padding: 30px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.06);
  margin-bottom: 30px;
}

.profile-info {
  text-align: center;
  margin-bottom: 25px;
}

.profile-name {
  font-size: 28px;
  font-weight: 700;
  color: #2d3748;
  margin: 0 0 8px;
}

.profile-badges {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 8px;
  margin-bottom: 12px;
}

.badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 14px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 500;
  background: #f0f0f0;
  color: #555;
}

.badge-vendor {
  background: #e8eaf6;
  color: #3949ab;
}

.badge-role {
  background: #e8f5e9;
  color: #2e7d32;
}

.badge md-icon {
  font-size: 16px;
  width: 16px;
  height: 16px;
}

.profile-description {
  color: #718096;
  font-size: 14px;
  line-height: 1.6;
  max-width: 600px;
  margin: 0 auto;
}

/* Quick Stats */
.quick-stats {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 15px;
  border-top: 1px solid #edf2f7;
  padding-top: 20px;
}

.stat-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  border-radius: 12px;
  background: #f7fafc;
  transition: all 0.2s;
}

.stat-item:hover {
  background: #edf2f7;
  transform: translateY(-2px);
}

.stat-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 10px;
  background: #e8eaf6;
  color: #3949ab;
}

.stat-icon md-icon {
  font-size: 20px;
  width: 20px;
  height: 20px;
}

.stat-info {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.stat-label {
  font-size: 11px;
  color: #a0aec0;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  font-weight: 600;
}

.stat-value {
  font-size: 14px;
  font-weight: 600;
  color: #2d3748;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* ========== Profile Details Grid ========== */
.profile-details-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 24px;
  margin-bottom: 30px;
}

.detail-card {
  background: #fff;
  border-radius: 16px;
  padding: 24px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.06);
  transition: all 0.2s;
}

.detail-card:hover {
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.1);
}

.detail-card-full {
  grid-column: 1 / -1;
}

.detail-card-header {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 18px;
  padding-bottom: 12px;
  border-bottom: 2px solid #edf2f7;
}

.detail-card-header md-icon {
  color: #667eea;
  font-size: 24px;
  width: 24px;
  height: 24px;
}

.detail-card-header h3 {
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: #2d3748;
}

.detail-card-body {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.detail-row {
  display: flex;
  justify-content: space-between;
  padding: 6px 0;
  border-bottom: 1px solid #f7fafc;
}

.detail-row:last-child {
  border-bottom: none;
}

.detail-label {
  font-size: 13px;
  color: #718096;
  font-weight: 500;
}

.detail-value {
  font-size: 13px;
  color: #2d3748;
  font-weight: 500;
  text-align: right;
}

.detail-value a {
  color: #667eea;
  text-decoration: none;
}

.detail-value a:hover {
  text-decoration: underline;
}

/* Signature Section */
.signature-body {
  align-items: center;
}

.signature-preview {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  width: 100%;
}

.signature-image {
  max-width: 250px;
  max-height: 80px;
  object-fit: contain;
  border: 1px solid #edf2f7;
  border-radius: 8px;
  padding: 12px;
  background: #fafafa;
}

.signature-date {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: #a0aec0;
}

.signature-date md-icon {
  font-size: 16px;
  width: 16px;
  height: 16px;
}

.signature-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 20px;
  color: #a0aec0;
}

.signature-empty md-icon {
  font-size: 40px;
  width: 40px;
  height: 40px;
  margin-bottom: 8px;
}

.signature-empty p {
  margin: 0;
  font-size: 13px;
}

.signature-btn {
  margin-top: 12px;
}

/* Roles Section */
.roles-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 12px;
}

.role-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  background: #f7fafc;
  border-radius: 10px;
  transition: all 0.2s;
}

.role-item:hover {
  background: #edf2f7;
  transform: translateX(4px);
}

.role-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 8px;
  background: #e8eaf6;
  color: #3949ab;
}

.role-icon md-icon {
  font-size: 18px;
  width: 18px;
  height: 18px;
}

.role-info {
  display: flex;
  flex-direction: column;
}

.role-name {
  font-size: 13px;
  font-weight: 600;
  color: #2d3748;
}

.role-description {
  font-size: 11px;
  color: #a0aec0;
}

.roles-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 30px;
  color: #a0aec0;
}

.roles-empty md-icon {
  font-size: 40px;
  width: 40px;
  height: 40px;
  margin-bottom: 8px;
}

.roles-empty p {
  margin: 0;
  font-size: 13px;
}

/* ========== Profile Actions Footer ========== */
.profile-actions-footer {
  display: flex;
  justify-content: center;
  gap: 16px;
  padding-top: 20px;
  border-top: 2px solid #edf2f7;
}

.profile-actions-footer .md-button {
  min-width: 180px;
}

/* ========== Responsive ========== */
@media (max-width: 1024px) {
  .profile-details-grid {
    grid-template-columns: 1fr;
  }

  .detail-card-full {
    grid-column: 1;
  }
}

@media (max-width: 768px) {
  .profile-header {
    padding: 20px 20px 60px 20px;
  }

  .profile-content {
    padding: 0 16px 24px;
    margin-top: -50px;
  }

  .profile-avatar .avatar-wrapper {
    width: 100px;
    height: 100px;
  }

  .profile-name {
    font-size: 22px;
  }

  .quick-stats {
    grid-template-columns: 1fr 1fr;
  }

  .profile-actions {
    position: static;
    display: flex;
    justify-content: center;
    margin-top: 16px;
  }

  .profile-actions-footer {
    flex-direction: column;
    align-items: center;
  }

  .profile-actions-footer .md-button {
    width: 100%;
  }

  .detail-row {
    flex-direction: column;
    gap: 4px;
  }

  .detail-value {
    text-align: left;
  }

  .roles-grid {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 480px) {
  .quick-stats {
    grid-template-columns: 1fr;
  }

  .stat-item {
    padding: 10px 12px;
  }

  .profile-badges {
    flex-direction: column;
    align-items: center;
  }
}
</style>